import type { OverrideStatusModalProps, CoResidentRow } from '../../types/modals'
import { useState, useEffect, useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { overrideApplicantStatus } from '../../services/applicationService'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import modalStyles from '../../styles/modals.module.css'


/** Co-applicant row shape for Applicant Lock Status (Angular `override-status.component`). */

const APPLICANT_STATUS_OPTIONS: Record<string, string> = {
  INCOMPLETE: 'Incomplete',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  PROPERTY_APPROVED: 'Property Approved',
  DENIED: 'Denied',
  EXPIRED: 'Expired',
  AWAITING: 'Awaiting',
  NOT_STARTED: 'Not Started',
  CANCELLED: 'Cancelled',
}

const APPLICATION_STATUS_OPTIONS: Record<string, string> = {
  INCOMPLETE: 'Incomplete',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  DENIED: 'Denied',
  EXPIRED: 'Expired',
  AWAITING: 'Awaiting',
  NOT_STARTED: 'Not Started',
  CANCELLED: 'Cancelled',
}

/** Full Angular applicantAllProgress map — filtered by selected Applicant Status (statusChange). */
const PROGRESS_STATUS_OPTIONS: Record<string, string> = {
  REGISTERED: 'User Registered',
  APPLICATION_STARTED: 'Application Started',
  APPLICATION_INCOMPLETE: 'Application Incomplete',
  APPLICATION_COMPLETE: 'Application Complete',
  APPLICATION_PAID: 'Application Paid',
  CREDIT_REPORT_PENDING: 'Credit Report Pending',
  CREDIT_REPORT_READY: 'Credit Report Ready',
  CREDIT_REPORT_FAILED: 'Credit Report Failed',
  UNDERWRITING_STARTED: 'Screening Started',
  UNDERWRITING_IN_PROGRESS: 'Screening In Process',
  UNDERWRITING_READY: 'Screening Ready',
  UNDERWRITING_SPLIT: 'Screening Split',
  APPROVED: 'Screening Approved',
  DENIED: 'Screening Denied',
  AWAITING_EXTERNAL: 'Awaiting External',
  IN_PROGRESS: 'In Progress',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
  CERTIFICATE_PAID: 'Access Paid',
  POLICY_ACTIVATION_PENDING: 'Policy Activation Pending',
  CERTIFICATE_ADJUSTMENT: 'Certificate Adjustment',
  CERTIFICATE_SENT: 'Certificate Sent',
  LEASE_INITIATED: 'Lease Initiated',
  RESUBMIT_CREDIT_REPORT: 'Resubmit Credit Report',
  ACTIVATION_COMPLETE: 'Activation Complete',
}

/** Progress options subset for selected Applicant Status (Angular statusChange). */
function getProgressOptionsForStatus(
  applicantStatus: string,
): Record<string, string> {
  if (!applicantStatus) return PROGRESS_STATUS_OPTIONS
  const out: Record<string, string> = {}
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      if (PROGRESS_STATUS_OPTIONS[k]) out[k] = PROGRESS_STATUS_OPTIONS[k]
    }
  }
  if (applicantStatus.includes('NOT_STARTED')) pick('REGISTERED')
  if (applicantStatus.includes('INCOMPLETE')) {
    pick(
      'APPLICATION_STARTED',
      'APPLICATION_INCOMPLETE',
      'APPLICATION_COMPLETE',
      'APPLICATION_PAID',
    )
  }
  if (applicantStatus.includes('PENDING')) {
    pick(
      'CREDIT_REPORT_PENDING',
      'CREDIT_REPORT_READY',
      'CREDIT_REPORT_FAILED',
      'UNDERWRITING_STARTED',
      'UNDERWRITING_IN_PROGRESS',
      'UNDERWRITING_READY',
      'UNDERWRITING_SPLIT',
    )
  }
  if (applicantStatus.includes('APPROVED')) {
    pick(
      'APPROVED',
      'CERTIFICATE_PAID',
      'POLICY_ACTIVATION_PENDING',
      'CERTIFICATE_ADJUSTMENT',
      'CERTIFICATE_SENT',
      'LEASE_INITIATED',
      'RESUBMIT_CREDIT_REPORT',
      'ACTIVATION_COMPLETE',
    )
  }
  if (applicantStatus.includes('AWAITING')) pick('AWAITING_EXTERNAL', 'IN_PROGRESS')
  if (applicantStatus.includes('DENIED')) pick('DENIED')
  if (applicantStatus.includes('EXPIRED')) pick('EXPIRED')
  if (applicantStatus.includes('CANCELLED')) pick('CANCELLED')
  return out
}

/** Map API `progress_status` (enum key or display label) to form key — Angular override-status `ngOnInit`. */
function resolveProgressStatusKey(progressStatus: string | undefined): string {
  if (!progressStatus) return ''
  if (PROGRESS_STATUS_OPTIONS[progressStatus]) return progressStatus
  const found = Object.entries(PROGRESS_STATUS_OPTIONS).find(([, label]) => label === progressStatus)
  return found ? found[0] : ''
}

export function OverrideStatusModal({
  applicationAllData,
  applicationData,
  onClose,
  onSuccess,
}: OverrideStatusModalProps) {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    new_applicant_status: '',
    new_progress_status: '',
    new_application_status: '',
    send_mail: false,
    remark: '',
  })

  /**
   * Same source as Angular `*ngFor="let coresident of applicationAllData?.co_resident_application | keyvalue"`.
   * `lock_applicants` on save = applicant IDs where lock = Yes (`applicant_lock === '1'` in API).
   */
  const coResidents = useMemo<CoResidentRow[]>(() => {
    const raw = applicationAllData?.co_resident_application
    if (!raw) return []
    if (Array.isArray(raw)) return raw as CoResidentRow[]
    return Object.values(raw) as CoResidentRow[]
  }, [applicationAllData])

  const [lockApplicants, setLockApplicants] = useState<Array<string | number>>([])

  useEffect(() => {
    const initial: Array<string | number> = []
    coResidents.forEach((co) => {
      if (co?.applicant_lock === '1' && co?.applicant_id != null) {
        initial.push(co.applicant_id)
      }
    })
    setLockApplicants(initial)
  }, [coResidents])

  /**
   * Angular `ngOnInit` patches `new_application_status`, `new_applicant_status`, and
   * `new_progress_status` from loaded data. Without this, React keeps `value=""` on
   * selects while only the option *label* shows the current status — Save then fails
   * validation even though the UI looks filled in.
   */
  useEffect(() => {
    if (!applicationData?.applicant_id || !applicationAllData) return
    const newApplicationStatus = applicationAllData.application_status ?? ''
    const newApplicantStatus = applicationData.applicant_status ?? ''
    const newProgressStatus = resolveProgressStatusKey(applicationData.progress_status)

    setFormData((prev) => ({
      ...prev,
      new_application_status: newApplicationStatus,
      new_applicant_status: newApplicantStatus,
      new_progress_status: newProgressStatus,
    }))
  }, [
    applicationData?.applicant_id,
    applicationData?.applicant_status,
    applicationData?.progress_status,
    applicationAllData?.application_status,
  ])

  const setApplicantLock = (applicantId: string | number, locked: boolean) => {
    setLockApplicants((prev) => {
      if (locked) {
        if (prev.includes(applicantId)) return prev
        return [...prev, applicantId]
      }
      return prev.filter((id) => id !== applicantId)
    })
  }

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value }
      // Angular `statusChange()` parity: when the Applicant Status changes the
      // Progress dropdown is rebuilt; clear any stale progress selection so the
      // user can pick from the new list.
      if (field === 'new_applicant_status') {
        next.new_progress_status = ''
      }
      return next
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const resolvedApplicationStatus =
      formData.new_application_status || applicationAllData?.application_status
    if (!resolvedApplicationStatus) {
      showError('Please select an Application Status')
      return
    }

    setLoading(true)
    try {
      const requestData: Record<string, unknown> = {
        new_application_status: resolvedApplicationStatus,
        send_mail: formData.send_mail,
        lock_applicants: lockApplicants,
      }

      const resolvedApplicantStatus =
        formData.new_applicant_status || applicationData?.applicant_status
      if (resolvedApplicantStatus) {
        requestData.new_applicant_status = resolvedApplicantStatus
      }
      if (formData.new_progress_status) {
        requestData.new_progress_status = formData.new_progress_status
      }
      if (formData.remark) {
        requestData.remark = formData.remark
      }

      const applicantId = applicationData.applicant_id;
      if (applicantId == null) {
        showError("Applicant id is missing");
        return;
      }

      await overrideApplicantStatus(applicantId, requestData)
      trackApplicationEvent('application_status_overridden', 'application_review', { application_status: String(resolvedApplicantStatus || '').toLowerCase() })
      showSuccess('Status Updated Successfully!')
      onSuccess()
      onClose()
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message
      showError(typeof msg === 'string' && msg ? msg : 'Failed to update status')
    } finally {
      setLoading(false)
    }
  }

  const currentApplicantStatus = applicationData?.applicant_status ?? "";
  const currentProgressStatus = applicationData?.progress_status ?? "";
  const applicantName = `${applicationData?.user_data?.first_name} ${applicationData?.user_data?.last_name}`;

  /** Friendly label for the Applicant Progress badge — the API field may carry
   *  either the key ("APPLICATION_STARTED") or the already-formatted label
   *  ("Application Started"). Look up first, fall back to the raw value. */
  const currentProgressDisplay =
    PROGRESS_STATUS_OPTIONS[currentProgressStatus] ?? currentProgressStatus;

  /** Filter the Progress dropdown to the subset tied to the user's chosen
   *  Applicant Status — falls back to current API status when nothing's picked
   *  (mirrors Angular's `applicationProgress` rebuild in `statusChange()`). */
  const progressOptions = getProgressOptionsForStatus(
    formData.new_applicant_status || currentApplicantStatus || '',
  )

  const tid = 'application-modal-override-status'

  return (
    <OnScreenModal
      isOpen={true}
      title="Override Status"
      onClose={onClose}
      maxWidth="max-w-3xl"
      hideClose
      headerVariant="centeredTitle"
      bodyScrollOnly
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      footer={
        <div className={modalStyles.modalFooterActionGroup}>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            data-testid={`${tid}-cancel-btn`}
            className={modalStyles.modalActionSecondary}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            data-testid={`${tid}-submit-button`}
            className={modalStyles.modalActionPrimary}
          >
            {loading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'Save'}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8" data-testid={`${tid}-form`}>

            {/* Applicant summary card */}
            <div
              className="rounded-lg border border-slate-200 bg-slate-50 p-4"
              data-testid={`${tid}-applicant-summary-card`}
            >
              <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
                <div className="grid grid-cols-[8.5rem_1fr] items-center gap-2" data-testid={`${tid}-applicant-id`}>
                  <span className="text-[#64748b] text-sm font-medium">Applicant ID:</span>
                  <span className="text-[#334155] font-bold text-sm">{applicationData?.applicant_id}</span>
                </div>
                <div className="grid grid-cols-[8.5rem_1fr] items-center gap-2" data-testid={`${tid}-application-id`}>
                  <span className="text-[#64748b] text-sm font-medium">Application ID:</span>
                  <span className="text-[#334155] font-bold text-sm">{applicationData?.fk_application_id || applicationData?.application_id}</span>
                </div>
                <div className="grid grid-cols-[8.5rem_1fr] items-center gap-2" data-testid={`${tid}-applicant-name`}>
                  <span className="text-[#64748b] text-sm font-medium">Applicant Name:</span>
                  <span className="text-[#334155] font-bold text-sm">{applicantName}</span>
                </div>
              </div>
            </div>

            {/* Row 2: Applicant Status & Progress */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid={`${tid}-status-progress-row`}>
              <div className="space-y-2">
                <label className="block text-sm text-[#64748b] font-medium" data-testid={`${tid}-applicant-status-label`}>
                  Applicant Status: <span className="font-bold text-[#64748b]">{APPLICANT_STATUS_OPTIONS[currentApplicantStatus] || currentApplicantStatus}</span>
                </label>
                <select
                  value={formData.new_applicant_status}
                  onChange={(e) => handleChange('new_applicant_status', e.target.value)}
                  data-testid={`${tid}-applicant-status-input`}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-700 focus:outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff]"
                >
                  <option value="">Select Applicant Status</option>
                  {Object.entries(APPLICANT_STATUS_OPTIONS).map(([key, value]) => (
                    <option
                      key={key}
                      value={key}
                      className={key === currentApplicantStatus ? 'hidden' : ''}
                    >
                      {value}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm text-[#64748b] font-medium" data-testid={`${tid}-progress-status-label`}>
                   Applicant Progress: <span className="font-bold text-[#64748b]">{currentProgressDisplay}</span>
                </label>
                <select
                  value={formData.new_progress_status}
                  onChange={(e) => handleChange('new_progress_status', e.target.value)}
                  data-testid={`${tid}-progress-status-input`}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-700 focus:outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff]"
                >
                  <option value="">Select Progress</option>
                  {Object.entries(progressOptions).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3: Application Status */}
            <div className="space-y-2 max-w-md">
              <label className="block text-sm text-[#64748b] font-medium" data-testid={`${tid}-application-status-label`}>
                Application Status: <span className="font-bold text-[#64748b]">{APPLICATION_STATUS_OPTIONS[String(applicationAllData?.application_status ?? "")] || applicationAllData?.application_status}</span>
              </label>
              <select
                value={formData.new_application_status}
                onChange={(e) => handleChange('new_application_status', e.target.value)}
                data-testid={`${tid}-application-status-input`}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-700 focus:outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff]"
                required
              >
                <option value="">Select Application Status</option>
                {Object.entries(APPLICATION_STATUS_OPTIONS).map(([key, value]) => (
                  <option
                    key={key}
                    value={key}
                    className={key === applicationAllData?.application_status ? 'hidden' : ''}
                  >
                    {value}
                  </option>
                ))}
              </select>
            </div>

            {/* Row 4: Applicant Lock Status — Angular iterates `co_resident_application`, not the single active applicant */}
            <div className="space-y-3" data-testid={`${tid}-lock-status-section`}>
              <h4 className="text-[#64748b] text-sm font-bold" data-testid={`${tid}-lock-status-title`}>Applicant Lock Status</h4>
              {coResidents.length === 0 ? (
                <p className="text-sm text-slate-500 italic" data-testid={`${tid}-lock-status-empty`}>No applicants in co-resident list.</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {coResidents.map((co) => {
                    const id = co.applicant_id
                    const rowName =
                      `${co.user_data?.first_name ?? ''} ${co.user_data?.last_name ?? ''}`.trim() ||
                      `Applicant ${id}`
                    const statusLabel = co.applicant_status ?? ''
                    const isLocked = lockApplicants.includes(id)
                    return (
                      <div key={String(id)} className="flex flex-wrap items-center gap-4" data-testid={`${tid}-row-${id}`}>
                        <span className="text-[#334155] text-sm min-w-[12rem]">
                          {rowName} [{statusLabel}]
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setApplicantLock(id, true)}
                            data-testid={`${tid}-row-${id}-lock-yes-btn`}
                            className={`px-4 py-1.5 text-sm font-medium transition-colors rounded border border-gray-300 ${
                              isLocked ? 'bg-[#1597ff] text-white border-[#1597ff]' : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setApplicantLock(id, false)}
                            data-testid={`${tid}-row-${id}-lock-no-btn`}
                            className={`px-4 py-1.5 text-sm font-medium transition-colors rounded border border-gray-300 ${
                              !isLocked ? 'bg-[#1597ff] text-white border-[#1597ff]' : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Row 5: Remark */}
            <div className="space-y-2">
              <label className="block text-sm text-[#64748b] font-medium" data-testid={`${tid}-remark-label`}>
                Remark <span className="font-normal text-slate-400">(Optional)</span>
              </label>
              <textarea
                value={formData.remark}
                onChange={(e) => handleChange('remark', e.target.value)}
                data-testid={`${tid}-remark-input`}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded text-sm text-gray-700 focus:outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff] resize-none"
                rows={3}
              />
            </div>

      </form>
    </OnScreenModal>
  )
}
